import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchWithAuth } from '../services/api';
import { logger } from '../services/logger';

export interface PorteiroShift {
  id: string;
  schedule_id: string;
  location_id: string;
  location_name?: string;
  porteiro_id: string;
  porteiro_name?: string;
  shift_date: string;
  shift_start_time: string;
  shift_end_time: string;
  scan_interval_minutes: number;
  actual_start_time?: string;
  actual_end_time?: string;
  status: 'pending' | 'active' | 'completed' | 'abandoned';
  expected_scans: number;
  completed_scans: number;
  missed_scans: number;
  last_scan_at?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PorteiroScan {
  id: string;
  shift_id: string;
  location_id: string;
  porteiro_id: string;
  scanned_at: string;
  latitude?: number;
  longitude?: number;
  device_info?: any;
  photo_path?: string;
  notes?: string;
}

export interface PorteiroEvent {
  id: string;
  type: 'porteiro_shift_started' | 'porteiro_shift_completed' | 'porteiro_scan' | 'porteiro_shift_abandoned' | 'system';
  description: string;
  location?: string;
  time: string;
  timestamp: string;
  status: 'success' | 'warning';
  customerId?: string;
  shiftId?: string;
  porteiroName?: string;
}

interface UsePorteiroEventStreamResult {
  porteiroEvents: PorteiroEvent[];
  activeShifts: PorteiroShift[];
  recentScans: PorteiroScan[];
  isLoading: boolean;
  error: Error | null;
  refresh: () => void;
}

const POLL_INTERVAL = 120000; // 120 seconds (2 minutes) - SSE provides real-time updates, polling is just backup
const MAX_EVENTS = 50;

// Helper to format event time
const formatEventTime = (eventDate: Date): string => {
  return eventDate.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

export const usePorteiroEventStream = (
  customerId: string | null,
  enabled: boolean = true
): UsePorteiroEventStreamResult => {
  const [porteiroEvents, setPorteiroEvents] = useState<PorteiroEvent[]>([]);
  const [activeShifts, setActiveShifts] = useState<PorteiroShift[]>([]);
  const [recentScans, setRecentScans] = useState<PorteiroScan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const seenEventIdsRef = useRef<Set<string>>(new Set());

  // Fetch active shifts
  const fetchActiveShifts = async (): Promise<PorteiroShift[]> => {
    try {
      const response = await fetchWithAuth('/api/v1/porteiro-shifts/active');
      if (!response.ok) {
        logger.warn('usePorteiroEventStream', `Failed to fetch active shifts: ${response.status}`);
        // If 403, user doesn't have permission - this is expected for non-managers
        if (response.status === 403) {
          return [];
        }
        throw new Error(`Failed to fetch active porteiro shifts: ${response.status}`);
      }
      const shifts = await response.json();
      logger.info('usePorteiroEventStream', 'Fetched active shifts', { count: shifts.length });
      return Array.isArray(shifts) ? shifts : [];
    } catch (err) {
      logger.error('usePorteiroEventStream', 'Failed to fetch active shifts', undefined, err as Error);
      return [];
    }
  };

  // Fetch pending shifts
  const fetchPendingShifts = async (): Promise<PorteiroShift[]> => {
    try {
      const response = await fetchWithAuth('/api/v1/porteiro-shifts/pending');
      if (!response.ok) {
        return [];
      }
      const shifts = await response.json();
      return Array.isArray(shifts) ? shifts : [];
    } catch (err) {
      logger.error('usePorteiroEventStream', 'Failed to fetch pending shifts', err);
      return [];
    }
  };

  // Fetch recent scans for a shift
  const fetchShiftScans = async (shiftId: string): Promise<PorteiroScan[]> => {
    try {
      const response = await fetchWithAuth(`/api/v1/porteiro-scans/shift/${shiftId}`);
      if (!response.ok) {
        return [];
      }
      const scans = await response.json();
      return Array.isArray(scans) ? scans : [];
    } catch (err) {
      logger.error('usePorteiroEventStream', 'Failed to fetch shift scans', err);
      return [];
    }
  };

  // Transform shift to event
  const transformShiftToEvent = (shift: PorteiroShift): PorteiroEvent | null => {
    const eventId = `shift-${shift.id}-${shift.status}`;

    // Skip if we've already seen this event
    if (seenEventIdsRef.current.has(eventId)) {
      return null;
    }

    let type: PorteiroEvent['type'];
    let description: string;
    let status: 'success' | 'warning';
    let timestamp: string;

    if (shift.status === 'active' && shift.actual_start_time) {
      type = 'porteiro_shift_started';
      description = `Turno iniciado - ${shift.porteiro_name || 'Porteiro'} em ${shift.location_name || 'local'}`;
      status = 'success';
      timestamp = shift.actual_start_time;
    } else if (shift.status === 'completed' && shift.actual_end_time) {
      type = 'porteiro_shift_completed';
      description = `Turno concluído - ${shift.porteiro_name || 'Porteiro'} (${shift.completed_scans}/${shift.expected_scans} verificações)`;
      status = 'success';
      timestamp = shift.actual_end_time;
    } else if (shift.status === 'abandoned' && shift.actual_end_time) {
      type = 'porteiro_shift_abandoned';
      description = `Turno abandonado - ${shift.porteiro_name || 'Porteiro'} (${shift.missed_scans} verificações perdidas)`;
      status = 'warning';
      timestamp = shift.actual_end_time;
    } else {
      return null;
    }

    const eventDate = new Date(timestamp);
    const time = formatEventTime(eventDate);

    seenEventIdsRef.current.add(eventId);

    return {
      id: eventId,
      type,
      description,
      location: shift.location_name,
      time,
      timestamp,
      status,
      shiftId: shift.id,
      porteiroName: shift.porteiro_name,
    };
  };

  // Transform scan to event
  const transformScanToEvent = (scan: PorteiroScan, shift?: PorteiroShift): PorteiroEvent | null => {
    const eventId = `scan-${scan.id}`;

    // Skip if we've already seen this event
    if (seenEventIdsRef.current.has(eventId)) {
      return null;
    }

    const eventDate = new Date(scan.scanned_at);
    const time = formatEventTime(eventDate);

    seenEventIdsRef.current.add(eventId);

    return {
      id: eventId,
      type: 'porteiro_scan',
      description: `Verificação realizada - ${shift?.porteiro_name || 'Porteiro'} em ${shift?.location_name || 'local'}`,
      location: shift?.location_name,
      time,
      timestamp: scan.scanned_at,
      status: 'success',
      shiftId: scan.shift_id,
      porteiroName: shift?.porteiro_name,
    };
  };

  // Poll for updates
  const pollPorteiroData = useCallback(async () => {
    if (!enabled || !customerId) {
      logger.debug('usePorteiroEventStream', 'Polling skipped', { enabled, hasCustomerId: !!customerId });
      return;
    }

    logger.info('usePorteiroEventStream', 'Starting porteiro data poll', { customerId });

    try {
      setIsLoading(true);
      setError(null);

      // Fetch active and pending shifts
      const [active, pending] = await Promise.all([
        fetchActiveShifts(),
        fetchPendingShifts(),
      ]);

      logger.info('usePorteiroEventStream', 'Fetched shifts', {
        activeCount: active.length,
        pendingCount: pending.length
      });

      const allShifts = [...active, ...pending];
      setActiveShifts(active);

      // Transform shifts to events
      const shiftEvents: PorteiroEvent[] = [];
      for (const shift of allShifts) {
        const event = transformShiftToEvent(shift);
        if (event) {
          shiftEvents.push(event);
        }
      }

      // Fetch scans for active shifts
      const scanEvents: PorteiroEvent[] = [];
      const allScans: PorteiroScan[] = [];

      for (const shift of active) {
        const scans = await fetchShiftScans(shift.id);
        allScans.push(...scans);

        for (const scan of scans) {
          const event = transformScanToEvent(scan, shift);
          if (event) {
            scanEvents.push(event);
          }
        }
      }

      setRecentScans(allScans);

      // Merge and sort events
      const newEvents = [...shiftEvents, ...scanEvents]
        .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
        .slice(0, MAX_EVENTS);

      setPorteiroEvents((prev) => {
        const merged = [...newEvents, ...prev];
        const unique = merged.filter(
          (event, index, self) =>
            index === self.findIndex((e) => e.id === event.id)
        );
        return unique
          .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
          .slice(0, MAX_EVENTS);
      });

    } catch (err) {
      logger.error('usePorteiroEventStream', 'Polling error', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, customerId]);

  // Setup polling
  useEffect(() => {
    if (!enabled || !customerId) {
      return;
    }

    // Initial poll
    pollPorteiroData();

    // Setup interval
    pollTimerRef.current = setInterval(pollPorteiroData, POLL_INTERVAL);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, [enabled, customerId, pollPorteiroData]);

  const refresh = useCallback(() => {
    pollPorteiroData();
  }, [pollPorteiroData]);

  return {
    porteiroEvents,
    activeShifts,
    recentScans,
    isLoading,
    error,
    refresh,
  };
};
